import { DEFAULT_OUTPUT_PROFILE, outputProfileLabel, outputProfileOf, type OutputProfile } from '@/core/outputProfile';
import type {
  ApprovalState,
  DocumentVersionRow,
  MissingSignatory,
  SignatoryCandidate,
} from '@/core/models/approval.models';
import { PERIOD_STATUS_LABELS, roleRequiresRelationship } from '@/core/models/approval.models';
import { ReportTypeCode } from '@/core/models/reports.models';
import { reportsService } from '@/core/services/reports';
import { displayDate } from './shared';

/** The same (empty) list for every role that needs no relationship, so a select never sees a new array. */
export const NO_CANDIDATES: SignatoryCandidate[] = [];

/** ap.DocumentVersion.Status. */
export const DOCUMENT_STATUS_LABELS: Record<number, string> = {
  0: 'Requested',
  1: 'Generating',
  2: 'Generated',
  3: 'Approved',
  4: 'Final',
  5: 'Failed',
};

/** #143: the sign dates entered on the period's accounts report form, used to pre-fill missing signatures. */
export interface ReportSignDates {
  director: string | null;
  accountant: string | null;
}

export const NO_SIGN_DATES: ReportSignDates = { director: null, accountant: null };

/** One editable row on the signatories table (`key` is only the React key, it is never sent). */
export interface SignatoryRow {
  key: number;
  signatoryId: string | null;
  reportRole: string;
  reportContactId: string | null;
  personName: string | null;
  /** A dayjs (typed or picked), or the ISO string the server sent. */
  signatureDate: unknown;
}

/**
 * A signature date as the API may send it for a row that was set aside (Clear readiness keeps the signatory and blanks the
 * date): null, an empty string, or .NET's default date. All of these mean "not signed", and show as an empty date field.
 */
export function signatureDateOf(value: string | null | undefined): string | null {
  const text = `${value ?? ''}`.trim();
  return text === '' || text.startsWith('0001-01-01') ? null : text;
}

/** How many signatory rows have no signature date: those people still have to (re-)sign the accounts being approved. */
export function unsignedCount(rows: readonly Pick<SignatoryRow, 'signatureDate'>[]): number {
  return rows.filter((r) => !r.signatureDate).length;
}

/** AA-41 -> 41: report references are numbered in the order they were created. */
export function refNumber(refNo: string): number {
  const match = /(\d+)\s*$/.exec(refNo ?? '');
  return match ? Number(match[1]) : -1;
}

/** The next preparation/review stage the period can move to, with the button text. Null once approved. */
export function nextStage(periodStatus: number | undefined): { status: number; label: string } | null {
  switch (periodStatus) {
    case 0:
      return { status: 1, label: 'Start preparation' };
    case 1:
      return { status: 2, label: 'Mark ready for review' };
    case 2:
      return { status: 3, label: 'Start review' };
    case 3:
      return { status: 4, label: 'Approve accounts' };
    default:
      return null;
  }
}

export function statusLabel(status: number | undefined): string {
  return status === undefined ? '' : (PERIOD_STATUS_LABELS[status] ?? '');
}

/** #145: `Version 2 - Approved - 21/09/2026`. */
export function documentLabel(d: DocumentVersionRow): string {
  const status = DOCUMENT_STATUS_LABELS[d.status] ?? `Status ${d.status}`;
  const generated = d.generatedAtUtc ? ` - ${displayDate(d.generatedAtUtc)}` : '';
  return `Version ${d.versionNo} - ${status}${generated}`;
}

/** #145: the version sent to the client, by number rather than its internal id. */
export function readyVersionLabel(state: ApprovalState | null, documents: DocumentVersionRow[]): string {
  const id = state?.readyDocumentVersionId;
  if (!id) {
    return '-';
  }
  const document = documents.find((d) => d.documentVersionId === id);
  if (!document) {
    return '-';
  }
  const profile = outputProfileOf(document);
  return profile === DEFAULT_OUTPUT_PROFILE
    ? `Version ${document.versionNo}`
    : `Version ${document.versionNo} (${outputProfileLabel(profile)})`;
}

/** Newest version first. */
export function sortDocuments(documents: DocumentVersionRow[]): DocumentVersionRow[] {
  return [...documents].sort((a, b) => b.versionNo - a.versionNo);
}

/** The versions of one kind of accounts (Full, Abridged or Filleted), newest first. */
export function documentsOfProfile(documents: DocumentVersionRow[], profile: OutputProfile): DocumentVersionRow[] {
  return documents.filter((d) => outputProfileOf(d) === profile);
}

/** Keep the chosen version while it is listed, else the first approved/final one, else the newest, else nothing. */
export function defaultDocumentId(documents: DocumentVersionRow[], current: string): string {
  if (documents.some((d) => d.documentVersionId === current)) {
    return current;
  }
  return (
    documents.find((d) => d.status === 3 || d.status === 4)?.documentVersionId ?? documents[0]?.documentVersionId ?? ''
  );
}

export interface CandidateLists {
  officers: SignatoryCandidate[];
  secretaries: SignatoryCandidate[];
}

/** #144: the lists are built once per load, so the select is always handed the same array. */
export function candidateLists(candidates: SignatoryCandidate[]): CandidateLists {
  return {
    officers: candidates.filter((c) => c.contactType === 1),
    secretaries: candidates.filter((c) => c.contactType === 2),
  };
}

/** Only the accountant needs no relationship; secretaries come from contact type 2, everyone else from type 1. */
export function candidatesForRole(role: string, lists: CandidateLists): SignatoryCandidate[] {
  if (!roleRequiresRelationship(role)) {
    return NO_CANDIDATES;
  }
  return role === 'SECRETARY' ? lists.secretaries : lists.officers;
}

/**
 * #143: adds a row for each signature the period still needs, and completes rows that have no date instead of
 * duplicating them. Returns the new rows and how many were changed or added (the toast shows only when that is > 0).
 */
export function fillMissingRows(
  current: SignatoryRow[],
  missing: MissingSignatory[],
  dates: ReportSignDates,
  lists: CandidateLists,
  newKey: () => number,
): { rows: SignatoryRow[]; changed: number } {
  const rows = current.map((r) => ({ ...r }));
  let changed = 0;
  for (const m of missing) {
    const date = m.reportRole === 'ACCOUNTANT' ? dates.accountant : dates.director;
    let needed = Math.max(1, m.minimumCount - m.signedCount);
    for (const row of rows) {
      if (needed > 0 && row.reportRole === m.reportRole && !row.signatureDate) {
        if (date) {
          row.signatureDate = date;
          changed++;
        }
        needed--;
      }
    }
    for (; needed > 0; needed--) {
      const candidates = candidatesForRole(m.reportRole, lists);
      rows.push({
        key: newKey(),
        signatoryId: null,
        reportRole: m.reportRole,
        reportContactId: candidates.length === 1 ? candidates[0].reportContactId : null,
        personName: null,
        signatureDate: date,
      });
      changed++;
    }
  }
  return { rows, changed };
}

/** The sign dates on the latest annual accounts report for this period; none if there is no report or it fails. */
export async function reportSignDates(clientId: string, periodId: string): Promise<ReportSignDates> {
  try {
    const [reports, trialBalances] = await Promise.all([
      reportsService.getReports(clientId, ReportTypeCode.Annual),
      reportsService.getTrialBalances(clientId),
    ]);
    const inPeriod = new Set(trialBalances.filter((t) => t.accountingPeriodId === periodId).map((t) => t.id));
    const latest = reports
      .filter((r) => inPeriod.has(r.currentTrialBalanceId))
      .sort((a, b) => refNumber(b.refNo) - refNumber(a.refNo))[0];
    if (!latest) {
      return NO_SIGN_DATES;
    }
    const detail = await reportsService.getReport(latest.id);
    return { director: detail.directorSignDate ?? null, accountant: detail.accountantSignDate ?? null };
  } catch {
    return NO_SIGN_DATES;
  }
}
